import React, { useCallback } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Button, CircularProgress, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { routes } from "config";
import { PageHeader } from "shared/components/Headers/PageHeader";
import { BlockSelection } from "./Sections/BlockSelection";
import { BlockTree } from "./Sections/BlockTree";
import { BlockForm } from "./Sections/BlockForm";
import { CoolsetPageLoader } from "shared/components/Loader/CoolsetLoader";
import { useMainSurvey } from "../hooks/useMainSurvey";
import { PublishButton } from "modules/Publications/components/PublishButton";
import { Publications } from "modules/Publications/publications";
import { SurveyStatus } from "modules/Surveys/components/SurveyStatus";
import { TableEdit } from "./Sections/TableEdit";
import { useCopyAndPasteBlocks } from "../hooks/useCopyAndPasteBlocks";

const MainSurveyEditor = () => {
  const { surveyId: mainSurveyId } = useParams();
  const navigate = useNavigate();

  // TODO - handle no surveyId in url
  const mainSurveyStore = useMainSurvey(mainSurveyId!);

  const { mainSurvey, isLoading, updatedDurationString, setMainSurvey, isSaving, isTableEditOpen } = mainSurveyStore;
  const copyAndPasteBlocksFromTableStore = useCopyAndPasteBlocks();

  const updateMainSurveyPublication = useCallback(
    (unpublishedAppId?: string | number, newPublication?: Publications.Publication) => {
      setMainSurvey((old) => ({
        ...old!,
        publications: newPublication
          ? [...old!.publications.filter((p) => p.application.id !== newPublication.application.id), newPublication]
          : old!.publications.filter((p) => p.application.id !== unpublishedAppId),
      }));
    },
    [setMainSurvey]
  );

  if (!mainSurvey && !isLoading) {
    return <div>Survey not found</div>;
  }

  return (
    <div className="layout-main-survey-editor">
      {isLoading ? (
        <CoolsetPageLoader />
      ) : (
        <>
          <PageHeader
            pageName="survey"
            title={mainSurvey?.title}
            leftElement={<SurveyStatus publications={mainSurvey?.publications} />}
            actions={
              <>
                {isSaving ? (
                  <CircularProgress size={20} />
                ) : (
                  <Typography color="text.secondary">{updatedDurationString}</Typography>
                )}
                <Button
                  startIcon={<VisibilityOutlinedIcon />}
                  onClick={() => navigate(routes.surveyPreview(mainSurveyId!))}
                >
                  Preview
                </Button>
                <PublishButton
                  publications={mainSurvey?.publications || []}
                  surveyId={mainSurveyId}
                  onPublishChange={updateMainSurveyPublication}
                />
              </>
            }
          />
          <div className="editor-section">
            <BlockSelection mainSurveyStore={mainSurveyStore} />
            <BlockTree mainSurveyStore={mainSurveyStore} />
            <BlockForm mainSurveyStore={mainSurveyStore} />

            {isTableEditOpen && (
              <TableEdit mainSurveyStore={mainSurveyStore} copyAndPasteBlocksStore={copyAndPasteBlocksFromTableStore} />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MainSurveyEditor;
